import React, { ReactElement, useState } from "react";
import {
    AiOutlineBorder,
    AiOutlineAlignCenter, AiOutlineAlignLeft, AiOutlineAlignRight,
    AiOutlineCloseCircle,
} from "react-icons/ai";
import { SiShadow } from "react-icons/si";
import Styles from "../Editor.module.scss";
import {
    ElementSettings,
} from "./base";

export enum AttributeSize {
    SMALL = "sm",
    MEDIUM = "md",
    LARGE = "lg",
}

export enum Align {
    LEFT = "left",
    CENTER = "center",
    RIGHT = "right",
}

export const BUTTON_CLASS = {
    [AttributeSize.SMALL]: "border-gray-500 border rounded-1",
    [AttributeSize.MEDIUM]: "border-gray-400 border rounded-1",
    [AttributeSize.LARGE]: "border-gray-200 border rounded-1",
};

export interface MediaSettings extends ElementSettings {
    src?: string;
    altText?: string;
    width?: number;
    height?: number;
    shadow?: AttributeSize;
    border?: AttributeSize;
    align?: Align;
}

export interface Props {
    settings: MediaSettings;
    onSetValue: (settings: MediaSettings) => void;
    onRemove: () => void;
    children?: ReactElement | string | (ReactElement | string)[];
}

const SIZE_NEXT = {
    undefined: AttributeSize.SMALL,
    [AttributeSize.SMALL]: AttributeSize.MEDIUM,
    [AttributeSize.MEDIUM]: AttributeSize.LARGE,
    [AttributeSize.LARGE]: undefined,
};

export const SHADOW_CLASS = {
    [AttributeSize.SMALL]: "shadow-sm",
    [AttributeSize.MEDIUM]: "shadow",
    [AttributeSize.LARGE]: "shadow-lg",
};

export const BORDER_CLASS = {
    [AttributeSize.SMALL]: "border rounded-1",
    [AttributeSize.MEDIUM]: "border rounded-3",
    [AttributeSize.LARGE]: "border rounded-4",
};

export const ALIGN_CLASS = {
    [Align.LEFT]: "",
    [Align.CENTER]: "mx-auto",
    [Align.RIGHT]: "ms-auto",
};

const ALIGN_ICON = {
    [Align.LEFT]: <AiOutlineAlignLeft />,
    [Align.CENTER]: <AiOutlineAlignCenter />,
    [Align.RIGHT]: <AiOutlineAlignRight />,
};

const ALIGN_NEXT = {
    [Align.LEFT]: Align.CENTER,
    [Align.CENTER]: Align.RIGHT,
    [Align.RIGHT]: Align.LEFT,
};

export function MediaToolbar({
    settings, onSetValue, onRemove, children,
}: Props): ReactElement {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [currentWidth, setWidth] = useState<string | undefined>(settings.width?.toString());
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [currentHeight, setHeight] = useState<string | undefined>(settings.height?.toString());

    const resize = (width?: number, height?: number) => {
        onSetValue({
            ...settings,
            width,
            height,
        });
    };

    const setValue = (value: Partial<MediaSettings>) => {
        onSetValue({
            ...settings,
            ...value,
        });
    };
    return (
        <>
            <div
                className={`bg-blue-dark rounded position-absolute top-0
                                        d-flex text-white align-items-center
                                        ${settings.align === Align.RIGHT ? "end-0" : "start-0"}
                                        ${Styles["editable-image-resizer"]}`}
            >
                <button
                    className={`btn btn-sm text-white btn-sm py-2 px-2 ${
                        settings.border ? BUTTON_CLASS[settings.border] : ""}`}
                    type="button"
                    title="Add a border"
                    onClick={() => setValue({ border: SIZE_NEXT[settings.border ?? "undefined"] })}
                >
                    <AiOutlineBorder />
                </button>
                <button
                    className={`btn btn-sm text-white btn-sm py-2 px-2 ms-1 ${
                        settings.shadow ? BUTTON_CLASS[settings.shadow] : ""}`}
                    type="button"
                    title="Add a shadow"
                    onClick={() => setValue({ shadow: SIZE_NEXT[settings.shadow ?? "undefined"] })}
                >
                    <SiShadow />
                </button>
                <button
                    className="btn btn-sm text-white btn-sm py-2 px-2 ms-1"
                    type="button"
                    title="Add a shadow"
                    onClick={() => setValue({ align: ALIGN_NEXT[settings.align ?? Align.LEFT] })}
                >
                    {ALIGN_ICON[settings.align ?? Align.LEFT]}
                </button>

                {children}

                <button
                    className="btn btn-sm text-white btn-sm py-1 px-2"
                    type="button"
                    onClick={() => resize(100)}
                >
                    Small
                </button>
                <button
                    className="btn btn-sm text-white btn-sm py-1 px-2"
                    type="button"
                    onClick={() => resize(512)}
                >
                    Medium
                </button>
                <button
                    className="btn btn-sm text-white btn-sm py-1 px-2"
                    type="button"
                    onClick={() => resize(1024)}
                >
                    Large
                </button>
                <small className="ms-5 text-gray-400">Custom</small>
                <input
                    placeholder="width"
                    className="bg-blue-dark border-0 text-white form-control input-image-resize"
                    type="number"
                    onChange={evt => setWidth(evt.target.value)}
                />
                <input
                    placeholder="height"
                    className="bg-blue-dark border-0 text-white form-control input-image-resize"
                    type="number"
                    onChange={evt => setHeight(evt.target.value)}
                />
                <button
                    className="btn btn-sm border-blue-light text-white btn-sm py-1 px-2 m-0"
                    type="button"
                    onClick={() => resize(
                        (!currentWidth || currentWidth === "auto") ? undefined : parseInt(currentWidth, 10),
                        (!currentHeight || currentHeight === "auto") ? undefined : parseInt(currentHeight, 10),
                    )}
                >
                    Resize
                </button>

            </div>
            <button
                className="btn btn-blue-dark btn-sm fs-4 text-white btn-sm py-1 px-2
                            position-absolute top-0 start-100 opacity-50 translate-middle"
                type="button"
                onClick={onRemove}
            >
                <AiOutlineCloseCircle />
            </button>
        </>
    );
}

MediaToolbar.defaultProps = {
    children: undefined,
};
